import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    Validators,
} from '@angular/forms';
import {
    ParameterValidators,
    UiParameterCollectionComponent,
} from '@components/shared/ui-parameter-collection/ui-parameter-collection.component';
import { UiModalComponent } from '@components/ui-modal/ui-modal.component';
import {
    IJob,
    IProcess,
    IStartProcess,
    JobStartStrategy,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import { ProcessRobotsGridComponent } from '@pages/process/grids';
import {
    MAX_NUMBER_OF_JOBS,
    MAX_NUMBER_OF_RESULTS,
} from '@pages/shared/constants';
import {
    JobService,
    ProcessService,
    SessionsService,
} from '@services';
import { ISuggestValueData } from '@uipath/angular/components/ui-suggest';
import UiValidators from '@validators';

import cloneDeep from 'lodash-es/cloneDeep';
import {
    BehaviorSubject,
    of,
    Subject,
} from 'rxjs';
import {
    finalize,
    map,
    switchMap,
    takeUntil,
} from 'rxjs/operators';

@Component({
    templateUrl: './job.modal.html',
    styleUrls: ['./job.modal.scss'],
    providers: [JobService, ProcessService, SessionsService],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobModalComponent
    extends UiModalComponent<IJob>
    implements OnInit, OnDestroy {

    public JobStartStrategy = JobStartStrategy;
    public model: IStartProcess = {} as IStartProcess;
    public form!: FormGroup;
    public loading$ = new BehaviorSubject<boolean>(false);
    public environmentId = 0;
    public process?: IProcess;
    public maxNoOfJobs = MAX_NUMBER_OF_JOBS;

    @ViewChild(ProcessRobotsGridComponent, { static: true })
    public processRobotsGrid?: ProcessRobotsGridComponent;

    @ViewChild(UiParameterCollectionComponent, { static: true })
    public parameterCollectionComponent?: UiParameterCollectionComponent;

    private _processService = this._injector.get<ProcessService>(ProcessService);
    private _jobService = this._injector.get<JobService>(JobService);
    private _sessionsService = this._injector.get<SessionsService>(SessionsService);
    private _translate = this._injector.get<TranslateService>(TranslateService);
    private _fb = this._injector.get<FormBuilder>(FormBuilder);
    private _destroyed$ = new Subject();
    private _inputJob!: IJob;

    public get selectedRobots(): Array<number> {
        if (this.processRobotsGrid && this.processRobotsGrid.selectedRobots) {
            return this.processRobotsGrid.selectedRobots.map(robot => robot.id);
        }
        return [];
    }

    public get isTargetSpecificRobots(): boolean {
        return this.strategy === JobStartStrategy.SpecificRobots;
    }

    public get isTargetJobsCount(): boolean {
        return this.strategy === JobStartStrategy.JobsCount;
    }

    public get strategy() {
        return this.form && this.form.get('strategy')!.value;
    }
    public set strategy(target: string) {
        this.form.get('strategy')!.setValue(target);
        if (
            this._strategyRequiresRobots &&
            this.model.releaseKey
        ) {
            this.processRobotsGrid!.environmentId = this.environmentId;
        }
        this._setTargetConditionalValidation();
    }

    public get isInputJobProvided(): boolean {
        return this._inputJob.robot != null;
    }

    ngOnInit() {
        this.model.jobsCount = 1;
        this.model.releaseKey = '';

        // this._inputJob is used for enabling/disabling ability to select the Process
        this._inputJob = cloneDeep(this.context.data);
        const inputJob = this._inputJob;

        this.form = this._fb.group({
            releaseKey: [
                inputJob.release ?
                    [{ id: inputJob.release.id, text: inputJob.release.name }] :
                    [],
                UiValidators.required(),
            ],
            jobsCount: [this.model.jobsCount],
            strategy: [JobStartStrategy.SpecificRobots],
            arguments: [inputJob.release && inputJob.inputArguments || {}],
        }, {
                validators: () =>
                    this._strategyRequiresRobots &&
                        this.processRobotsGrid!.selectedRobots.length === 0 ?
                        ({
                            'noRobotsSelected': true,
                        }) : null,
            });

        if (inputJob.release != null) {
            // read from database the process information on inputJob
            const getRobot = (databaseProcessInfo: IProcess) => inputJob.robot != null ?
                this._sessionsService
                    .getNonAttendedRobotInEnvironment(databaseProcessInfo.environmentId, inputJob.robot.id)
                    .pipe(
                        map(s => ({
                            process: databaseProcessInfo,
                            sessions: s.data,
                        })),
                    ) :
                // job didn't have a robot, return an empty array
                of({ process: databaseProcessInfo, sessions: [] });

            this._processService
                .getInstance(inputJob.release.id)
                .pipe(
                    switchMap(databaseProcessInfo => getRobot(databaseProcessInfo)),
                    takeUntil(this._destroyed$),
                )
                .subscribe(({ process, sessions }) => {
                    this.process = process;

                    this.environmentId = this.process.environmentId;
                    this.model.releaseKey = this.process.key;

                    if (inputJob.robot != null) {
                        // set environmentId on the grid, which causes the grid to load the robots
                        this.processRobotsGrid!.environmentId = this.process.environmentId;

                        // preselect the robot on the grid IF it is still in the environment
                        if (sessions.length === 1) {
                            const robot = { ...inputJob.robot, sessionId: inputJob.robot.id };
                            this.processRobotsGrid!.selectedRobots = [robot];
                        }
                    }
                });
        }

        this._setTargetConditionalValidation();
    }

    ngOnDestroy() {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    public processSearchFactory = (
        query: string = '',
        step: number = MAX_NUMBER_OF_RESULTS,
        skip = 0,
    ) => this._processService.suggest(query, step, skip)

    public onProcessSelected(event: ISuggestValueData<IProcess>) {
        const process = event.data!;
        this.process = process;

        const argumentInput = this.form.get('arguments')!;
        argumentInput.clearValidators();

        if (this.process.arguments) {
            argumentInput.setValidators(
                ParameterValidators.required(this.process.arguments.input),
            );
        }

        argumentInput.updateValueAndValidity(
            argumentInput.value,
        );

        this.model.releaseKey = process.key;
        this.environmentId = process.environmentId;

        if (this._strategyRequiresRobots) {
            this.processRobotsGrid!.environmentId = this.environmentId;
        }

        this._setTargetConditionalValidation();
    }

    onSubmit() {
        if (
            !this.form.valid ||
            (!this.parameterCollectionComponent || this.parameterCollectionComponent.isOpen) ||
            this.loading$.value
        ) {
            return;
        }

        this.loading$.next(true);

        this._jobService
            .startJobs({
                releaseKey: this.model.releaseKey,
                strategy: this.strategy,
                robotIds: this.isTargetSpecificRobots ? this.selectedRobots : [],
                jobsCount: this.isTargetSpecificRobots ? 0 : this.form.get('jobsCount')!.value,
                inputArguments: this.form.get('arguments')!.value,
            })
            .pipe(
                finalize(
                    () => this.loading$.next(false),
                ),
            )
            .subscribe(() => {
                this._snack.info(this._translate.instant('job_modal_confirmation_message'));
                this._dialogRef.close();
            });
    }

    private _setTargetConditionalValidation() {
        const jobsCountControl = this.form.get('jobsCount')!;
        const validators = this.isTargetSpecificRobots ?
            null :
            [Validators.required, UiValidators.range(1, MAX_NUMBER_OF_JOBS)];

        jobsCountControl.setValidators(validators);
        jobsCountControl.updateValueAndValidity();

        this.form.updateValueAndValidity();
    }

    private get _strategyRequiresRobots(): boolean {
        return this.strategy === JobStartStrategy.SpecificRobots;
    }
}
